import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthStateService } from './auth-state.service';
import { AuthUser, ForgotPasswordResponse, LoginPayload, LoginResponse, ResetPasswordResponse, VerifyOtpResponse } from './auth.model';
import { finalize, map, Observable, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private authState = inject(AuthStateService);
  private basePath = 'auth'

  login(payload: LoginPayload): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`/${this.basePath}/login`, payload).pipe(
      switchMap(res => this.http.get<AuthUser>('/users/me').pipe(
        tap(me => {
          this.authState.setSession(me);
          this.router.navigate([me.first_login ? '/set-password' : '/']);
        }),
        map(() => res),
      )),
    );
  }

  logout(): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`/${this.basePath}/logout`, {}).pipe(
      // finalize (not tap): clear the local session even if the call fails,
      // so the user is never stuck "logged in" in the UI.
      finalize(() => this.authState.clearSession()),
    );
  }

  // First login — replaces the emailed temporary password. Authenticated via the
  // session cookie; the backend keeps the session alive, so no re-login is needed.
  setFirstLoginPassword(password: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `/${this.basePath}/set-first-login-password`,
      { password },
    ).pipe(
      tap(() => this.markFirstLoginDone()),
    );
  }

  markFirstLoginDone(): void {
    const user = this.authState.currentUser();
    if (user) this.authState.setSession({ ...user, first_login: false });
  }

  sendCode(email: string): Observable<ForgotPasswordResponse> {
    return this.http.post<ForgotPasswordResponse>(
      `/${this.basePath}/forgot-password`,
      { email },
    );
  }

  // Step 2 — verify OTP, receive reset token
  verifyOtp(email: string, code: string): Observable<VerifyOtpResponse> {
    return this.http.post<VerifyOtpResponse>(
      `/${this.basePath}/verify-reset-otp`,
      { email, code },
    );
  }

  // Step 3 — reset password using the short-lived reset token
  // Reset token goes in Authorization header manually — NOT via the interceptor
  // because it's not a session token
  resetPassword(newPassword: string, resetToken: string): Observable<ResetPasswordResponse> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${resetToken}`,
    });

    return this.http.post<ResetPasswordResponse>(
      `/${this.basePath}/reset-password`,
      { password: newPassword },
      { headers },
    );
  }

}
